import type { ReactNode } from "react";

interface Props {
  title: string;
  subtitle?: string;
  /** HTTP method of the route this screen is bound to. */
  method?: string;
  /** BFF path this screen is bound to. */
  path?: string;
  /** Primary + secondary actions, aligned right of the title. */
  actions?: ReactNode;
  /** Small status chips (lane, environment, record state) under the title. */
  meta?: ReactNode;
  /** Parent trail, e.g. "Partner payables /". */
  breadcrumb?: ReactNode;
}

/** The header every screen opens with.
 *
 *  Twenty-three pages render this component, which is why it is the single
 *  highest-leverage surface in the console — and why its signature is only
 *  ever WIDENED, never changed. `method` / `path` keep working exactly as
 *  they did at all 21 existing call sites.
 *
 *  What changed is where that pair LANDS. It used to print a full-width line
 *  reading `Bound to: GET /v2/admin/bff/treasury/summary` directly under the
 *  subtitle — third in the reading order, above the actual content, on every
 *  screen. That is a debugging fact: it tells an operator which endpoint to
 *  quote when a screen misbehaves, and tells them nothing at all the rest of
 *  the time. It is now a recessive monospace chip on the right of the title
 *  row. Still on the page, still copyable, no longer the third thing read.
 *
 *  It is not hidden behind a disclosure because the operator's own brief kept
 *  "current host / API base" on the must-stay-visible list, and a route is
 *  the same class of fact. Demoted, not removed. */
export function PageHeader({
  title,
  subtitle,
  method,
  path,
  actions,
  meta,
  breadcrumb,
}: Props) {
  return (
    <header style={{ marginBottom: 20 }}>
      {breadcrumb !== undefined && (
        <div style={{ fontSize: 12.5, color: "var(--fg-dim)", marginBottom: 5 }}>{breadcrumb}</div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: "1 1 380px", minWidth: 0 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 640,
              letterSpacing: -0.02,
              color: "var(--fg)",
            }}
          >
            {title}
          </h1>
          {subtitle && (
            <p
              style={{
                margin: "5px 0 0",
                color: "var(--fg-dim)",
                fontSize: 13.5,
                maxWidth: "78ch",
              }}
            >
              {subtitle}
            </p>
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            flex: "0 0 auto",
          }}
        >
          {actions}
          {method && path && (
            <code
              title={`This screen reads ${method} ${path}`}
              className="g-mono"
              style={{
                fontSize: 10.5,
                color: "var(--fg-dim)",
                background: "var(--bg-inset)",
                border: "1px solid var(--border-faint)",
                borderRadius: "var(--r-sm)",
                padding: "3px 8px",
                whiteSpace: "nowrap",
                maxWidth: "42ch",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {method} {path}
            </code>
          )}
        </div>
      </div>

      {meta !== undefined && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            marginTop: 12,
          }}
        >
          {meta}
        </div>
      )}
    </header>
  );
}
